import { useEffect, useState } from "react";
import { loadSavedFilters, saveFilters } from "@/lib/filterUtils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pagination } from "@/components/ui/pagination";
import IssueFilters from "./IssueFilters";

// Define issue type
interface Issue {
  reportId: string;
  category: string;
  location: {
    lat: number;
    lng: number;
  };
  dateReported: string;
  status: "open" | "in-progress" | "closed" | "SPAM" | "Rejected";
  severity?: "high" | "medium" | "low";
  severityScore?: number;
  aiReason?: string;
  aiSpamReason?: string;
  name?: string;
  phoneNumber?: string;
  designation?: string;
}

interface Filters {
  category: string;
  status: string;
  startDate?: Date;
  endDate?: Date;
  severityScore?: number;
  userType?: string;
  ward?: string;
}

const IssueTable = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [reportedData, setReportedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>(() => {
    // Load filters from localStorage on component mount
    const savedFilters = loadSavedFilters();
    return savedFilters || {
      category: "all",
      status: "all",
      userType: "all",
      ward: "all",
    };
  });

  const itemsPerPage = 100;

  // Format date by trimming "T" and everything after "."
  const formatDate = (dateString: string) => {
    if (!dateString) return "—";

    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // Get day suffix (st, nd, rd, th)
  const getDaySuffix = (day: number) => {
    if (day >= 11 && day <= 13) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // Format category display by replacing underscores with spaces
  const formatCategoryDisplay = (category: string) => {
    return category.replace(/_/g, ' ');
  };

  // Check if designation column should be shown (only for C1 users)
  const shouldShowDesignationColumn = () => {
    return filters.userType === "c1";
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowClick = (reportId: string) => {
    navigate(`/issues/${reportId}`);
  };

  // Generate random severity for demo purposes
  const getRandomSeverity = (): "high" | "medium" | "low" => {
    const severities: ("high" | "medium" | "low")[] = ["high", "medium", "low"];
    return severities[Math.floor(Math.random() * severities.length)];
  };

  // Render severity badge based on severityScore
  const renderSeverityBadge = (severityScore?: number) => {
    if (!severityScore) {
      return (
        <Badge
          variant="outline"
          className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-500 dark:border-gray-800/30"
        >
          —
        </Badge>
      );
    }

    if (severityScore > 75) {
      return (
        <Badge
          variant="outline"
          className="bg-red-900 text-red-100 border-red-800 dark:bg-red-950/50 dark:text-red-200 dark:border-red-900/50"
        >
          {severityScore}
        </Badge>
      );
    }

    if (severityScore >= 50 && severityScore <= 75) {
      return (
        <Badge
          variant="outline"
          className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-500 dark:border-red-800/30"
        >
          {severityScore}
        </Badge>
      );
    }

    if (severityScore >= 21 && severityScore <= 50) {
      return (
        <Badge
          variant="outline"
          className="bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-500 dark:border-orange-800/30"
        >
          {severityScore}
        </Badge>
      );
    }

    if (severityScore < 20) {
      return (
        <Badge
          variant="outline"
          className="bg-orange-50 text-orange-600 border-orange-100 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800/20"
        >
          {severityScore}
        </Badge>
      );
    }

    return <Badge variant="outline">{severityScore}</Badge>;
  };

  // Render severity badge with tooltip
  const renderSeverityBadgeWithTooltip = (issue: any) => {
    const badge = renderSeverityBadge(issue.severityScore);

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center">{badge}</div>
          </TooltipTrigger>
          <TooltipContent className="bg-slate-900 text-white border-slate-700 shadow-lg">
            {issue.aiReason ? (
              <p className="max-w-xs text-sm leading-relaxed">
                {issue.aiReason}
              </p>
            ) : (
              <p className="max-w-xs text-sm text-slate-300">
                No AI reason available
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Render status badge
  const renderStatusBadge = (
    status:
      | "New"
      | "NEW"
      | "IN_REVIEW"
      | "RESOLVED"
      | "SPAM"
      | "Rejected"
      | "REJECTED"
  ) => {
    switch (status) {
      case "NEW":
        return (
          <div className="flex justify-center">
            <Badge
              variant="outline"
              className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-500 dark:border-blue-800/30"
            >
              New
            </Badge>
          </div>
        );
      case "IN_REVIEW":
        return (
          <div className="flex justify-center">
            <Badge
              variant="outline"
              className="bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-500 dark:border-orange-800/30"
            >
              In Review
            </Badge>
          </div>
        );
      case "RESOLVED":
        return (
          <div className="flex justify-center">
            <Badge
              variant="outline"
              className="bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/30"
            >
              Resolved
            </Badge>
          </div>
        );
      case "REJECTED":
        return (
          <div className="flex justify-center">
            <Badge
              variant="outline"
              className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-500 dark:border-red-800/30"
            >
              Rejected
            </Badge>
          </div>
        );
      case "SPAM":
        return (
          <div className="flex justify-center">
            <Badge
              variant="outline"
              className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-500 dark:border-gray-800/30"
            >
              SPAM
            </Badge>
          </div>
        );
      default:
        return (
          <div className="flex justify-center">
            <Badge>{status}</Badge>
          </div>
        );
    }
  };

  const handleFilterChange = (newFilters: Filters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    
    // Save filters to localStorage
    saveFilters(newFilters);
  };

  const handleExport = async (exportFilters: Filters) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found");

      const url = new URL(
        `${import.meta.env.VITE_BE_URL}/api/v1/reports/download-csv`
      );

      // Add filters to URL
      if (exportFilters.category !== "all") {
        url.searchParams.append("category", exportFilters.category);
      }
      if (exportFilters.status !== "all") {
        url.searchParams.append("status", exportFilters.status);
      }
      if (exportFilters.startDate) {
        url.searchParams.append(
          "startDate",
          exportFilters.startDate.toISOString().split("T")[0]
        );
      }
      if (exportFilters.endDate) {
        url.searchParams.append(
          "endDate",
          exportFilters.endDate.toISOString().split("T")[0]
        );
      }
      if (exportFilters.severityScore !== undefined && exportFilters.severityScore > 0 && !isNaN(exportFilters.severityScore)) {
        url.searchParams.append(
          "severityScore",
          exportFilters.severityScore.toString()
        );
      }
      if (exportFilters.userType) {
        url.searchParams.append("userType", exportFilters.userType);
      }
      if (exportFilters.ward) {
        url.searchParams.append("ward", exportFilters.ward);
      }

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to export data");
      }

      const data = await response.json();

      if (data.success && data.data.downloadUrl) {
        // Create a temporary link to download the file
        const link = document.createElement("a");
        link.href = data.data.downloadUrl;
        link.download = data.data.filename || "reports.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success(
          `CSV exported successfully! Total reports: ${data.data.totalReports}`
        );
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to export data"
      );
      console.error("Export error:", error);
    }
  };

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setIsLoading(true);

        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found");

        const url = new URL(`${import.meta.env.VITE_BE_URL}/api/v1/reports`);
        url.searchParams.append("page", currentPage.toString());
        url.searchParams.append("limit", itemsPerPage.toString());

        // Add filters to URL
        if (filters.category !== "all") {
          url.searchParams.append("category", filters.category);
        }
        if (filters.status !== "all") {
          url.searchParams.append("status", filters.status);
        }
        if (filters.startDate) {
          url.searchParams.append(
            "startDate",
            filters.startDate.toISOString().split("T")[0]
          );
        }
        if (filters.endDate) {
          url.searchParams.append(
            "endDate",
            filters.endDate.toISOString().split("T")[0]
          );
        }
        if (filters.severityScore !== undefined && filters.severityScore > 0 && !isNaN(filters.severityScore)) {
          url.searchParams.append(
            "severityScore",
            filters.severityScore.toString()
          );
        }
        if (filters.userType) {
          url.searchParams.append("userType", filters.userType);
        }
        if (filters.ward) {
          url.searchParams.append("ward", filters.ward);
        }

        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch reports");
        }

        const data = await response.json();
        // Add random severity to each report
        const reportsWithSeverity = data.data.reports.map((report: any) => ({
            ...report,
            severity: getRandomSeverity(),
          }));
        setReportedData(reportsWithSeverity);
        setTotalPages(data.data.pagination.pages);
        setTotalItems(data.data.pagination.total || 0);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        console.error("Fetch reports error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [currentPage, filters]);

  // Skeleton loader for loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <IssueFilters
          onFilterChange={handleFilterChange}
          onExport={handleExport}
          initialFilters={filters}
        />
        <div className="bg-card border border-border rounded-lg">
          <div className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <IssueFilters
        onFilterChange={handleFilterChange}
        onExport={handleExport}
        initialFilters={filters}
      />

      <div className="bg-card border border-border rounded-lg">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Reported Issues</h3>
              <p className="text-sm text-muted-foreground">
                Showing {reportedData.length} of {totalItems} issues
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">Report ID</TableHead>
                  <TableHead className="text-center">Name</TableHead>
                  {shouldShowDesignationColumn() && (
                    <TableHead className="text-center">Designation</TableHead>
                  )}
                  <TableHead className="text-center">Phone Number</TableHead>
                  <TableHead className="text-center">Category</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Severity Score</TableHead>
                  <TableHead className="text-center">Date Reported</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reportedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={shouldShowDesignationColumn() ? 9 : 8} className="text-center py-8">
                      <p className="text-muted-foreground">No issues found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  reportedData.map((issue: any) => (
                    <TableRow
                      key={issue.reportId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(issue.id)}
                    >
                      <TableCell className="text-center font-medium">
                        {issue.reportId}
                      </TableCell>
                      <TableCell className="text-center">
                        {issue.name || "—"}
                      </TableCell>
                      {shouldShowDesignationColumn() && (
                        <TableCell className="text-center">
                          {issue.designation || "—"}
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        {issue.phoneNumber || "—"}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{formatCategoryDisplay(issue.category)}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {renderStatusBadge(issue.status)}
                      </TableCell>
                      <TableCell className="text-center">
                        {renderSeverityBadgeWithTooltip(issue)}
                      </TableCell>
                      <TableCell className="text-center">
                        {formatDate(issue.createdAt)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(issue.id);
                          }}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IssueTable;
